import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@radix-ui/react-icons';
import { StatusBadge, Text, useSonnerToast } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import { LaunchesService } from '../../services/launchesApi';
import type { Launch } from '../../types';
import { ALLOCATION_STATUS_BADGE } from '../launch-unit-status';
import { mapUnitRowToInput, type UnitRowData } from '../create-launch/schema';
import { AddUnitForm } from '../create-launch/UnitFormControls';
import { formatUnitPrice } from '../LaunchUnitCard';

interface EditLaunchUnitsSectionProps {
  agencyId: string;
  launch: Launch;
  onUnitsChanged: () => void;
}

const EMPTY_DRAFT: UnitRowData = {
  property_id: '', property_label: '', label: '', tower: '', floor: '', typology: '', list_price: '',
};

/**
 * Manages the unit inventory of an existing launch: link additional
 * properties in, or remove units that haven't been allocated yet.
 */
export function EditLaunchUnitsSection({ agencyId, launch, onUnitsChanged }: EditLaunchUnitsSectionProps) {
  const { t } = useTranslation('features/launches');
  const { showSuccess } = useSonnerToast();
  const { properties } = useAgencyProperties(agencyId);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<UnitRowData>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [removingUnitId, setRemovingUnitId] = useState<string | null>(null);

  const usedPropertyIds = useMemo(
    () => new Set(launch.units.map((u) => u.property_id).filter(Boolean)),
    [launch.units],
  );
  const availableProperties = useMemo(
    () => properties.filter((p) => !usedPropertyIds.has(p.id)).map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel })),
    [properties, usedPropertyIds],
  );
  const usedTowers = useMemo(() => [...new Set(launch.units.map((u) => u.tower).filter(Boolean))] as string[], [launch.units]);
  const usedTypologies = useMemo(() => [...new Set(launch.units.map((u) => u.typology).filter(Boolean))] as string[], [launch.units]);

  const update = (field: keyof UnitRowData, value: string) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handlePropertySelect = (propertyId: string | null) => {
    if (!propertyId) {
      setDraft((prev) => ({ ...prev, property_id: '', property_label: '' }));
      return;
    }
    const prop = availableProperties.find((p) => p.id === propertyId);
    setDraft((prev) => ({
      ...prev,
      property_id: propertyId,
      property_label: prop?.label ?? '',
      label: prev.label || prop?.label || '',
    }));
  };

  const handleAdd = async () => {
    if (!draft.label) return;
    setBusy(true);
    try {
      await LaunchesService.addUnits(agencyId, launch.launch_id, [mapUnitRowToInput(draft)]);
      showSuccess(t('edit.unitAdded'));
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      onUnitsChanged();
    } catch (error) {
      reportError(error, { message: t('errors.generic') });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (unitId: string) => {
    setRemovingUnitId(unitId);
    try {
      await LaunchesService.removeUnit(agencyId, launch.launch_id, unitId);
      showSuccess(t('edit.unitRemoved'));
      onUnitsChanged();
    } catch (error) {
      reportError(error, { message: t('errors.generic') });
    } finally {
      setRemovingUnitId(null);
    }
  };

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      <Text as="span" className="text-[length:var(--text-small)] font-[var(--font-semibold)] text-[var(--text-primary)]">
        {t('create.unitsSectionTitle')}
      </Text>

      {launch.units.length > 0 && (
        <div className="flex flex-col gap-[var(--space-2)]">
          {launch.units.map((unit) => {
            const removable = unit.allocation_status === 'available';
            return (
              <div
                key={unit.unit_id}
                className={cn(
                  'flex items-center justify-between gap-[var(--space-2)]',
                  'rounded-(--radius-md) border border-[var(--border-subtle)]',
                  'bg-[var(--surface-primary)] px-[var(--space-3)] py-[var(--space-2)]',
                )}
              >
                <div className="flex min-w-0 flex-col gap-[1px]">
                  <div className="flex items-center gap-[var(--space-2)]">
                    <Text as="span" className="text-[length:var(--text-small)] font-[var(--font-semibold)] text-[var(--text-primary)] truncate">
                      {unit.label}
                    </Text>
                    <StatusBadge className={ALLOCATION_STATUS_BADGE[unit.allocation_status]}>
                      {t(`status.${unit.allocation_status}`)}
                    </StatusBadge>
                  </div>
                  {unit.list_price ? (
                    <Text as="span" className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
                      {formatUnitPrice(unit.list_price)}
                    </Text>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(unit.unit_id)}
                  disabled={!removable || removingUnitId === unit.unit_id}
                  title={removable ? undefined : t('edit.removeUnitBlocked')}
                  className="shrink-0 text-[length:var(--text-small)] text-[var(--text-tertiary)] hover:text-[var(--status-error)] disabled:cursor-not-allowed disabled:opacity-40 px-[var(--space-2)] py-[var(--space-1)]"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <AddUnitForm
          draft={draft}
          propertyOptions={availableProperties}
          usedTowers={usedTowers}
          usedTypologies={usedTypologies}
          onUpdate={update}
          onPropertySelect={handlePropertySelect}
          onConfirm={() => { void handleAdd(); }}
          onCancel={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy}
          className={cn(
            'flex items-center justify-center gap-[var(--space-2)]',
            'rounded-(--radius-md) border border-dashed border-[var(--border-subtle)]',
            'px-[var(--space-4)] py-[var(--space-3)]',
            'text-[length:var(--text-body-sm)] font-[var(--font-medium)]',
            'text-[var(--seed-base)] bg-transparent',
            'hover:bg-[var(--surface-secondary)] hover:border-[var(--seed-base)]',
          )}
          style={{ transition: 'var(--transition-fast)' }}
        >
          <PlusIcon width={16} height={16} />
          {t('create.addUnit')}
        </button>
      )}
    </div>
  );
}
